import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingDown,
  TrendingUp,
  Search,
  ExternalLink,
  FileSpreadsheet,
  RefreshCw,
  Building2,
  Store,
  Calendar,
  Layers,
  ChevronLeft,
  ChevronRight,
  Barcode,
  ArrowRight,
  Clock,
  Sparkles,
} from 'lucide-react';
import {
  fetchMarketPriceChanges,
  fetchMarketPriceChangesKpis,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { cn } from '../ui/cn.js';

function formatDt(val: number | null | undefined): string {
  if (val === null || val === undefined || Number.isNaN(val)) return '—';
  return `${(val / 1000).toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} DT`;
}

export function MarketPriceChangesPage(): React.ReactElement {
  const [kind, setKind] = useState<'all' | 'depot' | 'competitor'>('all');
  const [sourceKey, setSourceKey] = useState<string>('all');
  const [direction, setDirection] = useState<'all' | 'down' | 'up'>('all');
  const [search, setSearch] = useState<string>('');
  const [dateQuick, setDateQuick] = useState<'all' | 'today' | '7d' | '30d'>('all');
  const [page, setPage] = useState<number>(1);
  const pageSize = 50;

  // Resolve start/end dates from quick pills
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    if (dateQuick === 'today') {
      const todayStr = now.toISOString().slice(0, 10);
      return { startDate: todayStr, endDate: todayStr };
    }
    if (dateQuick === '7d') {
      const past = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { startDate: past.toISOString().slice(0, 10), endDate: now.toISOString().slice(0, 10) };
    }
    if (dateQuick === '30d') {
      const past = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { startDate: past.toISOString().slice(0, 10), endDate: now.toISOString().slice(0, 10) };
    }
    return { startDate: undefined, endDate: undefined };
  }, [dateQuick]);

  // KPIs query
  const kpisQuery = useQuery({
    queryKey: ['market-price-changes-kpis'],
    queryFn: fetchMarketPriceChangesKpis,
    staleTime: 30_000,
  });

  // Main data query
  const changesQuery = useQuery({
    queryKey: [
      'market-price-changes',
      { kind, sourceKey, direction, search, startDate, endDate, page, pageSize },
    ],
    queryFn: () =>
      fetchMarketPriceChanges({
        kind,
        sourceKey: sourceKey !== 'all' ? sourceKey : undefined,
        direction: direction !== 'all' ? direction : undefined,
        search: search.trim() !== '' ? search.trim() : undefined,
        startDate,
        endDate,
        page,
        pageSize,
      }),
    staleTime: 15_000,
  });

  const kpis = kpisQuery.data;
  const data = changesQuery.data;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Build Excel export URL
  const exportUrl = useMemo(() => {
    const q = new URLSearchParams();
    if (kind !== 'all') q.set('kind', kind);
    if (sourceKey !== 'all') q.set('sourceKey', sourceKey);
    if (direction !== 'all') q.set('direction', direction);
    if (search.trim()) q.set('search', search.trim());
    if (startDate) q.set('startDate', startDate);
    if (endDate) q.set('endDate', endDate);
    const query = q.toString();
    return `/api/v1/market-price-changes/export-xlsx${query ? '?' + query : ''}`;
  }, [kind, sourceKey, direction, search, startDate, endDate]);

  return (
    <div className="space-y-5 animate-fade pb-10">
      {/* Top Page Header */}
      <PageHeader
        index="S44"
        eyebrow="Surveillance & Arbitrage · Veille Marché"
        title="Veille Prix Marché — Dépôts & Concurrents"
        purpose="Historique en temps réel des variations tarifaires chez vos grossistes (Santé Access, Omnipharm, Medigros, ParaConnect) et sur les sites concurrents (Pharma Shop, Parashop, Parafendri)."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <a href={exportUrl} download>
              <Button
                variant="outline"
                size="sm"
                className="font-semibold shadow-2xs gap-1.5 border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
              >
                <FileSpreadsheet className="h-4 w-4 text-emerald-700" />
                <span>Exporter en Excel</span>
              </Button>
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void kpisQuery.refetch();
                void changesQuery.refetch();
              }}
              disabled={changesQuery.isFetching}
              title="Rafraîchir les données"
            >
              <RefreshCw className={cn('h-4 w-4', changesQuery.isFetching && 'animate-spin text-emerald-700')} />
            </Button>
          </div>
        }
      />

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* KPI 1: Changements 24h */}
        <Panel className="p-3.5 border-border bg-card shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs text-ink-muted">
            <span className="font-semibold uppercase tracking-wider text-[10px]">Dernières 24h</span>
            <Clock className="h-4 w-4 text-blue-600" />
          </div>
          <div className="mt-2">
            <div className="text-xl font-black text-ink">
              {kpis ? kpis.total24h.toLocaleString('fr-FR') : '—'}
            </div>
            <div className="text-[11px] text-ink-muted mt-0.5">mouvements récents</div>
          </div>
        </Panel>

        {/* KPI 2: Changements 7j */}
        <Panel className="p-3.5 border-border bg-card shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs text-ink-muted">
            <span className="font-semibold uppercase tracking-wider text-[10px]">Derniers 7 jours</span>
            <Calendar className="h-4 w-4 text-indigo-600" />
          </div>
          <div className="mt-2">
            <div className="text-xl font-black text-ink">
              {kpis ? kpis.total7d.toLocaleString('fr-FR') : '—'}
            </div>
            <div className="text-[11px] text-ink-muted mt-0.5">sur la semaine</div>
          </div>
        </Panel>

        {/* KPI 3: Baisses de Prix */}
        <Panel className="p-3.5 border-emerald-200 bg-emerald-50/50 shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs text-emerald-800">
            <span className="font-semibold uppercase tracking-wider text-[10px]">Baisses (-%)</span>
            <TrendingDown className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="mt-2">
            <div className="text-xl font-black text-emerald-800">
              {kpis ? kpis.totalDrops.toLocaleString('fr-FR') : '—'}
            </div>
            <div className="text-[11px] text-emerald-700/80 mt-0.5">opportunités / remises</div>
          </div>
        </Panel>

        {/* KPI 4: Hausses de Prix */}
        <Panel className="p-3.5 border-rose-200 bg-rose-50/50 shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs text-rose-800">
            <span className="font-semibold uppercase tracking-wider text-[10px]">Hausses (+%)</span>
            <TrendingUp className="h-4 w-4 text-rose-600" />
          </div>
          <div className="mt-2">
            <div className="text-xl font-black text-rose-800">
              {kpis ? kpis.totalHikes.toLocaleString('fr-FR') : '—'}
            </div>
            <div className="text-[11px] text-rose-700/80 mt-0.5">hausses de tarifs</div>
          </div>
        </Panel>

        {/* KPI 5: Dépôts / Grossistes */}
        <Panel className="p-3.5 border-sky-200 bg-sky-50/40 shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs text-sky-800">
            <span className="font-semibold uppercase tracking-wider text-[10px]">Dépôts B2B</span>
            <Building2 className="h-4 w-4 text-sky-600" />
          </div>
          <div className="mt-2">
            <div className="text-xl font-black text-sky-900">
              {kpis ? kpis.depotsCount.toLocaleString('fr-FR') : '—'}
            </div>
            <div className="text-[11px] text-sky-700/80 mt-0.5">mouvements grossistes</div>
          </div>
        </Panel>

        {/* KPI 6: Concurrents */}
        <Panel className="p-3.5 border-purple-200 bg-purple-50/40 shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between text-xs text-purple-800">
            <span className="font-semibold uppercase tracking-wider text-[10px]">Concurrents</span>
            <Store className="h-4 w-4 text-purple-600" />
          </div>
          <div className="mt-2">
            <div className="text-xl font-black text-purple-900">
              {kpis ? kpis.competitorsCount.toLocaleString('fr-FR') : '—'}
            </div>
            <div className="text-[11px] text-purple-700/80 mt-0.5">variations marché</div>
          </div>
        </Panel>
      </div>

      {/* Filters Control Bar */}
      <Panel className="p-4 space-y-3.5 border-border bg-card shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
          {/* Search Bar */}
          <div className="relative min-w-[260px] flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-ghost pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher par produit, marque ou code EAN..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-border bg-card text-xs text-ink placeholder:text-ink-ghost focus:border-emerald-600 focus:outline-none"
            />
          </div>

          {/* Quick Date Pills */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost mr-1">Période :</span>
            {(
              [
                { id: 'all', label: 'Toutes dates' },
                { id: 'today', label: "Aujourd'hui" },
                { id: '7d', label: '7 derniers jours' },
                { id: '30d', label: '30 jours' },
              ] as const
            ).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setDateQuick(p.id);
                  setPage(1);
                }}
                className={cn(
                  'rounded-lg px-2.5 py-1 text-xs font-semibold transition-all border',
                  dateQuick === p.id
                    ? 'border-emerald-600/30 bg-emerald-50 text-emerald-900 shadow-2xs'
                    : 'border-border/60 text-ink-soft hover:bg-cream/60',
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Secondary Filters Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Source Type Filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost mr-1">Type :</span>
            {(
              [
                { id: 'all', label: 'Tous', icon: Layers },
                { id: 'depot', label: 'Dépôts & Grossistes', icon: Building2 },
                { id: 'competitor', label: 'Concurrents', icon: Store },
              ] as const
            ).map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setKind(t.id);
                    setPage(1);
                  }}
                  className={cn(
                    'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all border',
                    kind === t.id
                      ? 'border-emerald-600/40 bg-emerald-100/70 text-emerald-950 shadow-2xs font-bold'
                      : 'border-border/60 text-ink-soft hover:bg-cream/60',
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>

          {/* Direction Filter */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost mr-1">Sens :</span>
            {(
              [
                { id: 'all', label: 'Toutes variations' },
                { id: 'down', label: 'Baisses (-%)' },
                { id: 'up', label: 'Hausses (+%)' },
              ] as const
            ).map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => {
                  setDirection(d.id);
                  setPage(1);
                }}
                className={cn(
                  'rounded-lg px-2.5 py-1 text-xs font-semibold transition-all border',
                  direction === d.id
                    ? d.id === 'down'
                      ? 'border-emerald-500 bg-emerald-100/80 text-emerald-900 font-bold'
                      : d.id === 'up'
                        ? 'border-rose-500 bg-rose-100/80 text-rose-900 font-bold'
                        : 'border-slate-500 bg-slate-100 text-slate-900 font-bold'
                    : 'border-border/60 text-ink-soft hover:bg-cream/60',
                )}
              >
                {d.label}
              </button>
            ))}
          </div>

          {/* Source Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-ink-ghost">Source :</span>
            <select
              value={sourceKey}
              onChange={(e) => {
                setSourceKey(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-border bg-card px-2.5 py-1 text-xs font-semibold text-ink focus:border-emerald-600 focus:outline-none"
            >
              <option value="all">Toutes les sources ({kpis?.sources?.reduce((acc, s) => acc + s.count, 0) ?? 0})</option>
              {kpis?.sources?.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.kind === 'depot' ? 'Dépôt' : 'Concurrent'} — {s.label} ({s.count.toLocaleString('fr-FR')})
                </option>
              ))}
            </select>
          </div>
        </div>
      </Panel>

      {/* Main Table Panel */}
      <Panel className="p-0 border-border bg-card shadow-xs overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-slate-50/60">
          <div className="text-xs font-bold text-ink flex items-center gap-2">
            <span>{total.toLocaleString('fr-FR')} variations de prix trouvées</span>
            {changesQuery.isFetching && <RefreshCw className="h-3.5 w-3.5 animate-spin text-emerald-600" />}
          </div>
          <div className="text-xs text-ink-muted">
            Page {page} sur {totalPages} ({pageSize} lignes/page)
          </div>
        </div>

        <TableShell>
          <Table>
            <THead>
              <Tr>
                <Th className="w-[80px]">Date</Th>
                <Th className="w-[180px]">Source &amp; Type</Th>
                <Th className="min-w-[260px]">Produit &amp; Marque</Th>
                <Th className="w-[140px]">Code Barre (EAN)</Th>
                <Th className="w-[110px] text-right">Ancien Prix</Th>
                <Th className="w-[110px] text-right">Nouveau Prix</Th>
                <Th className="w-[130px] text-right">Variation TTC</Th>
                <Th className="w-[100px] text-right">Action</Th>
              </Tr>
            </THead>
            <TBody>
              {items.length === 0 ? (
                <Tr>
                  <Td colSpan={8} className="py-12 text-center text-ink-muted">
                    {changesQuery.isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <RefreshCw className="h-4 w-4 animate-spin text-emerald-600" />
                        <span>Chargement des variations de prix...</span>
                      </div>
                    ) : (
                      'Aucune variation tarifaire ne correspond aux critères sélectionnés.'
                    )}
                  </Td>
                </Tr>
              ) : (
                items.map((c) => {
                  const isDrop = (c.diffTtc ?? 0) < 0;
                  const isDepot = c.sourceKind === 'depot';
                  const dateStr = c.detectedAt ? new Date(c.detectedAt).toLocaleDateString('fr-FR') : '—';
                  const timeStr = c.detectedAt ? new Date(c.detectedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';

                  return (
                    <Tr key={c.id} className="hover:bg-slate-50/70 transition-colors">
                      {/* Date */}
                      <Td className="whitespace-nowrap">
                        <div className="text-xs font-semibold text-ink">{dateStr}</div>
                        <div className="text-[10px] text-ink-ghost font-mono">{timeStr}</div>
                      </Td>

                      {/* Source & Type */}
                      <Td>
                        <div className="flex flex-col gap-1 items-start">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border',
                              isDepot
                                ? 'bg-sky-50 text-sky-800 border-sky-200'
                                : 'bg-purple-50 text-purple-800 border-purple-200',
                            )}
                          >
                            {isDepot ? <Building2 className="h-2.5 w-2.5" /> : <Store className="h-2.5 w-2.5" />}
                            <span>{isDepot ? 'Dépôt B2B' : 'Concurrent'}</span>
                          </span>
                          <span className="text-xs font-bold text-ink truncate max-w-[170px]" title={c.sourceLabel}>
                            {c.sourceLabel}
                          </span>
                        </div>
                      </Td>

                      {/* Product Name & Brand */}
                      <Td>
                        <div className="flex items-start gap-2.5">
                          {c.image && (
                            <img
                              src={c.image}
                              alt=""
                              className="h-9 w-9 rounded object-contain border border-border bg-white shrink-0 mt-0.5"
                              loading="lazy"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-semibold text-ink line-clamp-2" title={c.productName}>
                              {c.productName}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {c.brand && (
                                <span className="inline-block rounded px-1.5 py-0.2 bg-slate-100 text-slate-700 font-bold text-[10px] border border-slate-200">
                                  {c.brand}
                                </span>
                              )}
                              {c.catalogProduct && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">
                                  <Sparkles className="h-2.5 w-2.5" />
                                  <span>Lié catalogue</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Td>

                      {/* Barcode (EAN) */}
                      <Td className="whitespace-nowrap font-mono text-xs text-ink-soft">
                        {c.gtin ? (
                          <span className="inline-flex items-center gap-1 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200">
                            <Barcode className="h-3 w-3 text-ink-ghost" />
                            <span>{c.gtin}</span>
                          </span>
                        ) : (
                          <span className="text-ink-ghost text-[11px] italic">Non renseigné</span>
                        )}
                      </Td>

                      {/* Old Price */}
                      <Td className="text-right whitespace-nowrap text-xs text-ink-muted line-through">
                        {formatDt(c.oldPriceTtc)}
                      </Td>

                      {/* New Price */}
                      <Td className="text-right whitespace-nowrap text-xs font-extrabold text-ink">
                        {formatDt(c.newPriceTtc)}
                      </Td>

                      {/* Diff Badge */}
                      <Td className="text-right whitespace-nowrap">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-extrabold border',
                            isDrop
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-rose-50 text-rose-700 border-rose-200',
                          )}
                        >
                          {isDrop ? <TrendingDown className="h-3 w-3 shrink-0" /> : <TrendingUp className="h-3 w-3 shrink-0" />}
                          <span>
                            {c.diffTtc !== null ? (c.diffTtc > 0 ? `+${(c.diffTtc / 1000).toFixed(3)}` : (c.diffTtc / 1000).toFixed(3)) : '—'} DT
                          </span>
                          {c.diffPercent !== null && (
                            <span className="text-[10px] font-bold opacity-80">
                              ({c.diffPercent > 0 ? `+${c.diffPercent.toFixed(1)}` : c.diffPercent.toFixed(1)}%)
                            </span>
                          )}
                        </span>
                      </Td>

                      {/* Action */}
                      <Td className="text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          {c.url && (
                            <a
                              href={c.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 rounded text-ink-ghost hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                              title="Voir la fiche source"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                          {c.catalogProduct && (
                            <a
                              href={`/catalogue/marge-control?search=${encodeURIComponent(c.catalogProduct.sku)}`}
                              className="p-1 rounded text-ink-ghost hover:text-blue-700 hover:bg-blue-50 transition-colors"
                              title="Ouvrir dans Contrôle des Marges"
                            >
                              <ArrowRight className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </TBody>
          </Table>
        </TableShell>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-slate-50/50">
            <div className="text-xs text-ink-muted">
              Affichage de {items.length > 0 ? (page - 1) * pageSize + 1 : 0} à {Math.min(page * pageSize, total)} sur {total.toLocaleString('fr-FR')} variations
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                <span>Précédent</span>
              </Button>
              <span className="text-xs font-semibold px-2">
                Page {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <span>Suivant</span>
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
}
